/**
 * 精灵库 + 程序化怪物拼装
 * 输入：LLM 返回的 MonsterDesc
 * 输出：64x64 canvas 像素图 + 待机动画帧
 *
 * 设计：基于 scpId 哈希做种子，所有变体（体型/模板/配件/调色板/扰动）都是确定性的。
 * 同一 scpId 永远生成同一只怪 → 四宫格和详情页完全一致。
 * 4 个不同 scpId 走不同种子 → 四宫格 4 只互不相同。
 */

import palettesData from "../data/palettes.json";
import type { MonsterDesc, Palette } from "../shared/types";

interface PalettesDB {
  palettes: Palette[];
}

const PALETTES: Palette[] = (palettesData as PalettesDB).palettes;
const EXTENDED_PALETTES: Palette[] = [...PALETTES, ...generateExtraPalettes()];

// === 调色板（12 套）===
function generateExtraPalettes(): Palette[] {
  return [
    { name: "CyanPulse", main: "#00ddcc", sub: "#66f0e0", highlight: "#ccfff7", dark: "#006b62" },
    { name: "Magma", main: "#ff3366", sub: "#ff7799", highlight: "#ffbbcc", dark: "#99112a" },
    { name: "LimeShock", main: "#aaff00", sub: "#ccff66", highlight: "#eaffbb", dark: "#5a8a00" },
    { name: "Ultraviolet", main: "#7700ff", sub: "#aa55ff", highlight: "#d4aaff", dark: "#3a007a" },
  ];
}

export type BodyType = "humanoid" | "beast" | "abstract" | "multi_leg" | "winged" | "blob" | "tall" | "stout" | "snake" | "spider" | "crystal" | "fish";
export const BODY_TYPES: BodyType[] = ["humanoid", "beast", "abstract", "multi_leg", "winged", "blob", "tall", "stout", "snake", "spider", "crystal", "fish"];

// === 哈希：字符串 → 32-bit 整数 ===
function hashStr(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

// === 简易种子随机 ===
class Rng {
  private state: number;
  constructor(seed: number) {
    this.state = seed || 0xdeadbeef;
  }
  next(): number {
    let x = this.state;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.state = x >>> 0;
    return (this.state & 0x7fffffff) / 0x7fffffff;
  }
  int(min: number, max: number): number {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }
  pick<T>(arr: T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }
  pickByWeight<T>(items: T[], weights: number[]): T {
    const total = weights.reduce((s, w) => s + w, 0);
    let r = this.next() * total;
    for (let i = 0; i < items.length; i++) {
      r -= weights[i];
      if (r <= 0) return items[i];
    }
    return items[items.length - 1];
  }
}

// === 类型工具 ===
type RGB = [number, number, number, number];

function hexToRgb(hex: string): RGB {
  const h = hex.replace("#", "");
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
    255,
  ];
}

function dim(c: RGB, factor: number): RGB {
  return [Math.floor(c[0] * factor), Math.floor(c[1] * factor), Math.floor(c[2] * factor), c[3]];
}

function lighten(c: RGB, factor: number): RGB {
  return [
    Math.min(255, Math.floor(c[0] + (255 - c[0]) * factor)),
    Math.min(255, Math.floor(c[1] + (255 - c[1]) * factor)),
    Math.min(255, Math.floor(c[2] + (255 - c[2]) * factor)),
    c[3],
  ];
}

function hueShift(c: RGB, degrees: number): RGB {
  // 简化的 hue 偏移
  const rad = (degrees * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const m = [
    [cos + (1 - cos) / 3, (1 - cos) / 3 - sin / Math.sqrt(3), (1 - cos) / 3 + sin / Math.sqrt(3)],
    [(1 - cos) / 3 + sin / Math.sqrt(3), cos + (1 - cos) / 3, (1 - cos) / 3 - sin / Math.sqrt(3)],
    [(1 - cos) / 3 - sin / Math.sqrt(3), (1 - cos) / 3 + sin / Math.sqrt(3), cos + (1 - cos) / 3],
  ];
  const r = c[0] * m[0][0] + c[1] * m[0][1] + c[2] * m[0][2];
  const g = c[0] * m[1][0] + c[1] * m[1][1] + c[2] * m[1][2];
  const b = c[0] * m[2][0] + c[1] * m[2][1] + c[2] * m[2][2];
  return [
    Math.max(0, Math.min(255, Math.floor(r))),
    Math.max(0, Math.min(255, Math.floor(g))),
    Math.max(0, Math.min(255, Math.floor(b))),
    c[3],
  ];
}

function px(data: ImageData, x: number, y: number, c: RGB): void {
  if (x < 0 || y < 0 || x >= data.width || y >= data.height) return;
  const i = (y * data.width + x) * 4;
  data.data[i] = c[0];
  data.data[i + 1] = c[1];
  data.data[i + 2] = c[2];
  data.data[i + 3] = c[3];
}

function fillRect(data: ImageData, x: number, y: number, w: number, h: number, c: RGB): void {
  for (let yy = y; yy < y + h; yy++) {
    for (let xx = x; xx < x + w; xx++) {
      px(data, xx, yy, c);
    }
  }
}

function blankCanvas(): ImageData {
  const c = document.createElement("canvas");
  c.width = 64;
  c.height = 64;
  return c.getContext("2d")!.createImageData(64, 64);
}

// === 调色板 + 派生色 ===
interface PaletteEx extends Palette {
  mainC: RGB;
  subC: RGB;
  highC: RGB;
  darkC: RGB;
  eyeC: RGB;
  bodyC: RGB;
  bodyShadowC: RGB;
  bodyLightC: RGB;
}

function derivePalette(p: Palette, rng: Rng): PaletteEx {
  const mainC = hexToRgb(p.main);
  // 整体 hue 偏移 ±15°
  const hue = rng.int(-15, 15);
  const mainShift = hueShift(mainC, hue);
  return {
    ...p,
    mainC: mainShift,
    subC: hexToRgb(p.sub),
    highC: hexToRgb(p.highlight),
    darkC: hexToRgb(p.dark),
    eyeC: [255, 255, 255, 255],
    bodyC: mainShift,
    bodyShadowC: dim(mainShift, 0.7),
    bodyLightC: lighten(mainShift, 0.2),
  };
}

// === 通用程序化扰动 ===
function perturb(data: ImageData, rng: Rng, intensity: number, c: RGB): void {
  // 随机加一些噪点
  for (let i = 0; i < intensity; i++) {
    const x = rng.int(0, 63);
    const y = rng.int(0, 63);
    px(data, x, y, c);
  }
}

// === 配件库：每个配件有多种变体 ===

// 角
type HornStyle = "crown" | "single" | "pair" | "spiral" | "twin_dot" | "wide" | "none";
function drawHorn(data: ImageData, style: HornStyle, p: PaletteEx, rng: Rng): void {
  const hornC = dim(p.mainC, 0.3);
  switch (style) {
    case "crown":
      fillRect(data, 24, 4, 16, 2, p.highC);
      fillRect(data, 25, 2, 2, 2, p.highC);
      fillRect(data, 30, 1, 4, 3, p.highC);
      fillRect(data, 36, 2, 2, 2, p.highC);
      break;
    case "single":
      fillRect(data, 31, 1, 2, 5, hornC);
      fillRect(data, 30, 0, 4, 1, p.highC);
      break;
    case "pair":
      fillRect(data, 25, 2, 2, 6, hornC);
      fillRect(data, 37, 2, 2, 6, hornC);
      break;
    case "spiral":
      fillRect(data, 24, 4, 4, 4, hornC);
      fillRect(data, 23, 5, 1, 2, p.highC);
      fillRect(data, 36, 4, 4, 4, hornC);
      fillRect(data, 40, 5, 1, 2, p.highC);
      break;
    case "twin_dot":
      fillRect(data, 26, 4, 3, 3, hornC);
      fillRect(data, 35, 4, 3, 3, hornC);
      break;
    case "wide":
      fillRect(data, 22, 4, 20, 2, hornC);
      for (let i = 0; i < 5; i++) {
        fillRect(data, 24 + i * 4, 2, 1, 2, hornC);
      }
      break;
    case "none":
    default:
      break;
  }
}

// 眼型
type EyeStyle = "double" | "single" | "many" | "tall" | "x" | "closed" | "sparkle" | "big";
function drawEyes(data: ImageData, style: EyeStyle, p: PaletteEx, headX: number, headY: number): void {
  const eyeC = p.eyeC;
  const pupilC = [0, 0, 0, 255] as RGB;
  switch (style) {
    case "double":
      fillRect(data, headX + 5, headY + 7, 3, 4, eyeC);
      fillRect(data, headX + 12, headY + 7, 3, 4, eyeC);
      fillRect(data, headX + 6, headY + 8, 1, 2, pupilC);
      fillRect(data, headX + 13, headY + 8, 1, 2, pupilC);
      break;
    case "single":
      fillRect(data, headX + 8, headY + 7, 4, 4, eyeC);
      fillRect(data, headX + 9, headY + 8, 2, 2, pupilC);
      break;
    case "many":
      for (let i = 0; i < 5; i++) {
        fillRect(data, headX + 4 + i * 3, headY + 6 + (i % 2) * 3, 2, 2, eyeC);
      }
      break;
    case "tall":
      fillRect(data, headX + 5, headY + 5, 2, 7, eyeC);
      fillRect(data, headX + 13, headY + 5, 2, 7, eyeC);
      break;
    case "x":
      for (const [dx, dy] of [[0, 0], [1, 1], [2, 0], [0, 2], [2, 2]]) {
        px(data, headX + 5 + dx, headY + 8 + dy, eyeC);
        px(data, headX + 13 + dx, headY + 8 + dy, eyeC);
      }
      break;
    case "closed":
      for (let i = 0; i < 3; i++) {
        px(data, headX + 5 + i, headY + 9, eyeC);
        px(data, headX + 13 + i, headY + 9, eyeC);
      }
      break;
    case "sparkle":
      fillRect(data, headX + 5, headY + 7, 3, 4, eyeC);
      fillRect(data, headX + 12, headY + 7, 3, 4, eyeC);
      // 闪光点
      px(data, headX + 6, headY + 7, p.highC);
      px(data, headX + 13, headY + 7, p.highC);
      break;
    case "big":
      fillRect(data, headX + 4, headY + 6, 5, 5, eyeC);
      fillRect(data, headX + 11, headY + 6, 5, 5, eyeC);
      fillRect(data, headX + 6, headY + 8, 2, 2, pupilC);
      fillRect(data, headX + 13, headY + 8, 2, 2, pupilC);
      break;
  }
}

// 嘴
type MouthStyle = "smile" | "frown" | "open" | "fang" | "stitch" | "sealed" | "tongue";
function drawMouth(data: ImageData, style: MouthStyle, p: PaletteEx, headX: number, headY: number): void {
  const darkC = p.darkC;
  const redC = [200, 50, 80, 255] as RGB;
  switch (style) {
    case "smile":
      for (let i = 0; i < 5; i++) {
        px(data, headX + 6 + i, headY + 14 - (i < 2 || i > 2 ? 1 : 0), darkC);
      }
      break;
    case "frown":
      for (let i = 0; i < 5; i++) {
        px(data, headX + 6 + i, headY + 13 + (i < 2 || i > 2 ? 0 : 1), darkC);
      }
      break;
    case "open":
      fillRect(data, headX + 7, headY + 12, 6, 3, darkC);
      break;
    case "fang":
      fillRect(data, headX + 7, headY + 12, 6, 2, darkC);
      fillRect(data, headX + 7, headY + 14, 1, 2, p.eyeC);
      fillRect(data, headX + 12, headY + 14, 1, 2, p.eyeC);
      break;
    case "stitch":
      for (let i = 0; i < 5; i++) {
        px(data, headX + 6 + i, headY + 13, darkC);
        if (i % 2 === 0) px(data, headX + 6 + i, headY + 12, p.highC);
      }
      break;
    case "sealed":
      fillRect(data, headX + 6, headY + 13, 8, 1, p.highC);
      break;
    case "tongue":
      fillRect(data, headX + 7, headY + 12, 6, 2, darkC);
      fillRect(data, headX + 9, headY + 14, 2, 2, redC);
      break;
  }
}

// 翅膀
type WingStyle = "fairy" | "bat" | "feather" | "jelly" | "none";
function drawWings(data: ImageData, style: WingStyle, p: PaletteEx): void {
  if (style === "none") return;
  const wingC = p.subC;
  switch (style) {
    case "fairy":
      fillRect(data, 4, 18, 12, 6, wingC);
      fillRect(data, 2, 22, 14, 4, dim(wingC, 0.85));
      fillRect(data, 48, 18, 12, 6, wingC);
      fillRect(data, 48, 22, 14, 4, dim(wingC, 0.85));
      break;
    case "bat":
      fillRect(data, 4, 20, 10, 4, wingC);
      fillRect(data, 6, 16, 6, 4, wingC);
      fillRect(data, 2, 24, 12, 2, dim(wingC, 0.7));
      fillRect(data, 50, 20, 10, 4, wingC);
      fillRect(data, 52, 16, 6, 4, wingC);
      fillRect(data, 50, 24, 12, 2, dim(wingC, 0.7));
      break;
    case "feather":
      for (let i = 0; i < 3; i++) {
        fillRect(data, 6 + i * 2, 14 + i, 8 - i * 2, 4, wingC);
      }
      for (let i = 0; i < 3; i++) {
        fillRect(data, 50 + i * 2, 14 + i, 8 - i * 2, 4, wingC);
      }
      break;
    case "jelly":
      fillRect(data, 4, 18, 14, 4, wingC);
      fillRect(data, 6, 22, 10, 4, dim(wingC, 0.6));
      fillRect(data, 8, 26, 6, 2, dim(wingC, 0.5));
      fillRect(data, 46, 18, 14, 4, wingC);
      fillRect(data, 48, 22, 10, 4, dim(wingC, 0.6));
      fillRect(data, 50, 26, 6, 2, dim(wingC, 0.5));
      break;
  }
}

// === 体型模板（每个体型 3 个变体）===

// 人形 3 变体
function drawHumanoid(data: ImageData, variant: 0 | 1 | 2, p: PaletteEx, features: Set<string>): void {
  // 变体 0：标准人形，变体 1：高大，变体 2：矮胖
  const sizes = [
    { bodyW: 24, bodyH: 22, bodyX: 20, bodyY: 26, headW: 18, headH: 18, headX: 23, headY: 8, legH: 12 },
    { bodyW: 22, bodyH: 28, bodyX: 21, bodyY: 22, headW: 16, headH: 16, headX: 24, headY: 6, legH: 14 },
    { bodyW: 28, bodyH: 18, bodyX: 18, bodyY: 30, headW: 22, headH: 18, headX: 21, headY: 10, legH: 10 },
  ];
  const s = sizes[variant];
  fillRect(data, s.bodyX, s.bodyY, s.bodyW, s.bodyH, p.bodyC);
  fillRect(data, s.bodyX + s.bodyW - 6, s.bodyY, 6, s.bodyH, p.bodyShadowC);
  fillRect(data, s.bodyX + 2, s.bodyY + 2, 4, s.bodyH - 4, p.bodyLightC);

  fillRect(data, s.headX, s.headY, s.headH, s.headH, p.subC);
  fillRect(data, s.headX + 2, s.headY + 2, 3, s.headH - 4, p.highC);

  fillRect(data, s.bodyX + (s.bodyW - 6) / 2, s.bodyY + s.bodyH, 6, s.legH, p.bodyShadowC);
  fillRect(data, s.bodyX + s.bodyW / 2, s.bodyY + s.bodyH, 6, s.legH, p.bodyShadowC);

  // 触须（如果有）
  if (features.has("触须") || features.has("thread_tendril")) {
    for (let i = 0; i < 4; i++) {
      fillRect(data, 18 - i, 28 + i * 2, 2, 2, p.bodyShadowC);
      fillRect(data, 46 + i, 28 + i * 2, 2, 2, p.bodyShadowC);
    }
  }
}

// 兽形 3 变体
function drawBeast(data: ImageData, variant: 0 | 1 | 2, p: PaletteEx): void {
  const variants = [
    { bodyW: 36, bodyH: 18, bodyX: 14, bodyY: 26, headX: 46, headY: 18, legSpread: 10 },
    { bodyW: 40, bodyH: 22, bodyX: 12, bodyY: 24, headX: 44, headY: 14, legSpread: 12 },
    { bodyW: 30, bodyH: 14, bodyX: 18, bodyY: 30, headX: 46, headY: 22, legSpread: 8 },
  ];
  const s = variants[variant];
  fillRect(data, s.bodyX, s.bodyY, s.bodyW, s.bodyH, p.bodyC);
  fillRect(data, s.bodyX, s.bodyY, s.bodyW, 4, p.bodyShadowC);
  fillRect(data, s.bodyX, s.bodyY + s.bodyH - 4, s.bodyW, 4, p.bodyShadowC);

  fillRect(data, s.headX, s.headY, 16, 22, p.subC);
  fillRect(data, s.headX + 14, s.headY + 4, 4, 6, p.darkC);

  // 4 足
  for (let i = 0; i < 4; i++) {
    const x = s.bodyX + 2 + i * s.legSpread;
    fillRect(data, x, s.bodyY + s.bodyH, 6, 14, p.bodyShadowC);
    fillRect(data, x, s.bodyY + s.bodyH + 14, 8, 2, p.darkC);
  }
  // 尾
  fillRect(data, s.bodyX - 8, s.bodyY + 4, 8, 4, p.subC);
  fillRect(data, s.bodyX - 12, s.bodyY + 2, 4, 8, p.bodyShadowC);
}

// 抽象 3 变体
function drawAbstract(data: ImageData, variant: 0 | 1 | 2, p: PaletteEx): void {
  const variants = [
    { cx: 32, cy: 32, rW: 28, rH: 28 },
    { cx: 32, cy: 32, rW: 32, rH: 24 },
    { cx: 30, cy: 34, rW: 24, rH: 30 },
  ];
  const s = variants[variant];
  fillRect(data, s.cx - s.rW / 2, s.cy - s.rH / 2, s.rW, s.rH, p.bodyC);
  // 不规则边缘
  fillRect(data, s.cx - s.rW / 2 - 2, s.cy - 4, 4, 8, p.bodyC);
  fillRect(data, s.cx + s.rW / 2 - 2, s.cy - 4, 4, 8, p.bodyC);
  fillRect(data, s.cx - 4, s.cy - s.rH / 2 - 2, 8, 4, p.bodyC);
  fillRect(data, s.cx - 4, s.cy + s.rH / 2 - 2, 8, 4, p.bodyC);
  // 高光
  fillRect(data, s.cx - s.rW / 2 + 4, s.cy - s.rH / 2 + 4, 10, 6, p.bodyLightC);
  fillRect(data, s.cx + s.rW / 2 - 6, s.cy + s.rH / 2 - 6, 4, 4, p.bodyShadowC);

  // 触手
  for (let i = 0; i < 4; i++) {
    fillRect(data, 4, 24 + i * 5, 10, 2, p.bodyShadowC);
    fillRect(data, 50, 24 + i * 5, 10, 2, p.bodyShadowC);
  }
}

// 多足 3 变体
function drawMultiLeg(data: ImageData, variant: 0 | 1 | 2, p: PaletteEx): void {
  const variants = [
    { legs: 6, legW: 4, bodyW: 40, headW: 14 },
    { legs: 8, legW: 3, bodyW: 44, headW: 12 },
    { legs: 4, legW: 6, bodyW: 36, headW: 16 },
  ];
  const s = variants[variant];
  fillRect(data, 12, 28, s.bodyW, 16, p.bodyC);
  fillRect(data, 14, 30, s.bodyW - 4, 2, p.bodyLightC);

  fillRect(data, 6, 24, s.headW, 22, p.subC);
  fillRect(data, 8, 26, 4, 18, p.highC);

  // 多足
  for (let i = 0; i < s.legs; i++) {
    const x = 14 + (i * (s.bodyW - 4)) / (s.legs - 1);
    fillRect(data, Math.floor(x), 44, s.legW, 10, p.bodyShadowC);
    fillRect(data, Math.floor(x) - 1, 54, s.legW + 2, 2, p.darkC);
  }
}

// 带翅 3 变体
function drawWinged(data: ImageData, variant: 0 | 1 | 2, p: PaletteEx): void {
  const variants = [
    { bodyW: 20, wingSize: 1.0 },
    { bodyW: 24, wingSize: 1.2 },
    { bodyW: 18, wingSize: 0.8 },
  ];
  const s = variants[variant];
  fillRect(data, (64 - s.bodyW) / 2, 24, s.bodyW, 26, p.bodyC);
  fillRect(data, (64 - s.bodyW) / 2 + 2, 26, 4, 22, p.bodyLightC);

  const headX = (64 - s.bodyW) / 2;
  fillRect(data, headX + 2, 8, s.bodyW - 4, 16, p.subC);
  fillRect(data, headX + 4, 10, 3, 12, p.highC);
}

// 球状 3 变体
function drawBlob(data: ImageData, variant: 0 | 1 | 2, p: PaletteEx): void {
  const variants = [
    { cx: 32, cy: 34, r: 22 },
    { cx: 30, cy: 30, r: 20 },
    { cx: 34, cy: 36, r: 24 },
  ];
  const s = variants[variant];
  // 圆形（用矩形近似）
  const r = s.r;
  for (let y = -r; y <= r; y++) {
    for (let x = -r; x <= r; x++) {
      if (x * x + y * y < r * r) {
        const isTop = y < -r * 0.4;
        const isBottom = y > r * 0.4;
        const c = isTop ? p.bodyLightC : isBottom ? p.bodyShadowC : p.bodyC;
        fillRect(data, s.cx + x, s.cy + y, 1, 1, c);
      }
    }
  }
  // 触手
  for (let i = 0; i < 4; i++) {
    fillRect(data, s.cx - r - 8, s.cy - 8 + i * 6, 8, 2, p.bodyShadowC);
    fillRect(data, s.cx + r, s.cy - 8 + i * 6, 8, 2, p.bodyShadowC);
  }
}

// 高瘦 3 变体
function drawTall(data: ImageData, variant: 0 | 1 | 2, p: PaletteEx): void {
  const variants = [
    { bodyW: 16, bodyH: 36, bodyX: 24, bodyY: 18, headW: 14, headY: 6 },
    { bodyW: 14, bodyH: 40, bodyX: 25, bodyY: 16, headW: 12, headY: 4 },
    { bodyW: 20, bodyH: 32, bodyX: 22, bodyY: 20, headW: 16, headY: 8 },
  ];
  const s = variants[variant];
  fillRect(data, s.bodyX, s.bodyY, s.bodyW, s.bodyH, p.bodyC);
  fillRect(data, s.bodyX + s.bodyW - 4, s.bodyY, 4, s.bodyH, p.bodyShadowC);
  fillRect(data, s.bodyX + 2, s.bodyY + 2, 3, s.bodyH - 4, p.bodyLightC);

  fillRect(data, s.bodyX + 1, s.headY, s.headW, s.headW, p.subC);
  fillRect(data, s.bodyX + 3, s.headY + 2, 2, s.headW - 4, p.highC);

  // 触手
  for (let i = 0; i < 3; i++) {
    fillRect(data, 20 - i, 24 + i * 6, 2, 2, p.bodyShadowC);
    fillRect(data, 44 + i, 24 + i * 6, 2, 2, p.bodyShadowC);
  }
}

// 矮胖 3 变体
function drawStout(data: ImageData, variant: 0 | 1 | 2, p: PaletteEx): void {
  const variants = [
    { bodyW: 36, bodyH: 24, bodyX: 14, bodyY: 28, headW: 18, headY: 12 },
    { bodyW: 40, bodyH: 22, bodyX: 12, bodyY: 30, headW: 20, headY: 14 },
    { bodyW: 32, bodyH: 28, bodyX: 16, bodyY: 26, headW: 16, headY: 10 },
  ];
  const s = variants[variant];
  fillRect(data, s.bodyX, s.bodyY, s.bodyW, s.bodyH, p.bodyC);
  fillRect(data, s.bodyX + s.bodyW - 6, s.bodyY, 6, s.bodyH, p.bodyShadowC);
  fillRect(data, s.bodyX + 2, s.bodyY + 2, 6, s.bodyH - 4, p.bodyLightC);

  // 矮腿
  fillRect(data, s.bodyX + 4, s.bodyY + s.bodyH, 6, 6, p.bodyShadowC);
  fillRect(data, s.bodyX + s.bodyW - 10, s.bodyY + s.bodyH, 6, 6, p.bodyShadowC);

  fillRect(data, s.bodyX + (s.bodyW - s.headW) / 2, s.headY, s.headW, s.headW, p.subC);
  fillRect(data, s.bodyX + (s.bodyW - s.headW) / 2 + 2, s.headY + 2, 4, s.headW - 4, p.highC);
}

// 蛇形 3 变体
function drawSnake(data: ImageData, variant: 0 | 1 | 2, p: PaletteEx): void {
  const variants = [
    { bodyW: 40, bodyH: 10, bodyX: 12, bodyY: 32 },
    { bodyW: 44, bodyH: 14, bodyX: 10, bodyY: 30 },
    { bodyW: 36, bodyH: 12, bodyX: 14, bodyY: 32 },
  ];
  const s = variants[variant];
  fillRect(data, s.bodyX, s.bodyY, s.bodyW, s.bodyH, p.bodyC);
  fillRect(data, s.bodyX, s.bodyY, s.bodyW, 2, p.bodyShadowC);
  fillRect(data, s.bodyX, s.bodyY + s.bodyH - 2, s.bodyW, 2, p.bodyShadowC);
  // 头（右侧）
  fillRect(data, s.bodyX + s.bodyW, s.bodyY - 4, 8, s.bodyH + 8, p.subC);
  fillRect(data, s.bodyX + s.bodyW + 4, s.bodyY - 1, 2, 2, p.eyeC);
  fillRect(data, s.bodyX + s.bodyW + 5, s.bodyY + 4, 2, 1, p.darkC);
  // 尾（左侧，渐细）
  fillRect(data, s.bodyX - 6, s.bodyY + 2, 6, s.bodyH - 4, p.bodyShadowC);
  fillRect(data, s.bodyX - 2, s.bodyY + 4, 2, s.bodyH - 8, p.bodyC);
  // 变体 2：身体纹路
  if (variant === 2) {
    for (let i = 0; i < 4; i++) {
      fillRect(data, s.bodyX + 6 + i * 8, s.bodyY + 2, 4, s.bodyH - 4, p.subC);
    }
  }
}

// 蜘蛛 3 变体
function drawSpider(data: ImageData, variant: 0 | 1 | 2, p: PaletteEx): void {
  const variants = [
    { cx: 32, cy: 34, r: 14, legLen: 14 },
    { cx: 32, cy: 32, r: 16, legLen: 16 },
    { cx: 30, cy: 36, r: 12, legLen: 18 },
  ];
  const s = variants[variant];
  // 圆身（用矩形近似）
  for (let y = -s.r; y <= s.r; y++) {
    for (let x = -s.r; x <= s.r; x++) {
      if (x * x + y * y < s.r * s.r) {
        const c = y < -s.r * 0.3 ? p.bodyLightC : y > s.r * 0.3 ? p.bodyShadowC : p.bodyC;
        fillRect(data, s.cx + x, s.cy + y, 1, 1, c);
      }
    }
  }
  // 8 条腿
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2 + Math.PI / 8;
    for (let j = 1; j <= s.legLen; j++) {
      const t = j / s.legLen;
      const r = s.r + j * 1.2;
      const x = Math.round(s.cx + Math.cos(angle) * r);
      const y = Math.round(s.cy + Math.sin(angle) * r);
      if (x >= 0 && x < 64 && y >= 0 && y < 64) {
        const c = t < 0.6 ? p.bodyShadowC : p.bodyC;
        fillRect(data, x, y, 1, 1, c);
      }
    }
  }
  // 4 个眼（顶部一排）
  for (let i = 0; i < 4; i++) {
    fillRect(data, s.cx - 6 + i * 4, s.cy - 3, 2, 2, p.eyeC);
  }
}

// 水晶 3 变体
function drawCrystal(data: ImageData, variant: 0 | 1 | 2, p: PaletteEx): void {
  const variants = [
    { cx: 32, cy: 36, h: 28, w: 18 },
    { cx: 32, cy: 40, h: 36, w: 12 },
    { cx: 32, cy: 36, h: 22, w: 26 },
  ];
  const s = variants[variant];
  const halfW = s.w / 2;
  // 菱形/八面体形状
  for (let y = 0; y < s.h; y++) {
    const t = y / s.h;
    const w = Math.max(1, halfW * (1 - Math.abs(t - 0.5) * 2));
    const x = Math.round(s.cx - w);
    const wPx = Math.round(w * 2);
    fillRect(data, x, Math.round(s.cy - s.h / 2 + y), wPx, 1, p.bodyC);
  }
  // 上半：高光
  for (let y = 0; y < s.h / 2; y++) {
    const t = y / (s.h / 2);
    const w = halfW * (1 - t) * 0.4;
    fillRect(data, s.cx - w, s.cy - s.h / 2 + y, w * 2, 1, p.bodyLightC);
  }
  // 下半：暗面
  for (let y = s.h / 2; y < s.h; y++) {
    const t = (y - s.h / 2) / (s.h / 2);
    const w = halfW * t * 0.4;
    fillRect(data, s.cx + (halfW - w), s.cy - s.h / 2 + y, w * 2, 1, p.bodyShadowC);
  }
  // 变体 2：底部多个小晶体
  if (variant === 2) {
    fillRect(data, s.cx - 8, s.cy + s.h / 2 + 2, 4, 4, p.subC);
    fillRect(data, s.cx + 4, s.cy + s.h / 2 + 2, 4, 4, p.subC);
  }
}

// 鱼 3 变体
function drawFish(data: ImageData, variant: 0 | 1 | 2, p: PaletteEx): void {
  const variants = [
    { bodyW: 36, bodyH: 16, bodyX: 14, bodyY: 28 },
    { bodyW: 42, bodyH: 20, bodyX: 12, bodyY: 26 },
    { bodyW: 32, bodyH: 14, bodyX: 16, bodyY: 30 },
  ];
  const s = variants[variant];
  // 椭圆身体
  for (let y = 0; y < s.bodyH; y++) {
    const t = (y - s.bodyH / 2) / (s.bodyH / 2);
    if (Math.abs(t) > 1) continue;
    const w = s.bodyW / 2 * Math.sqrt(1 - t * t);
    fillRect(data, Math.round(s.bodyX + s.bodyW / 2 - w), s.bodyY + y, Math.round(w * 2), 1, p.bodyC);
  }
  // 尾鳍（右侧）
  fillRect(data, s.bodyX + s.bodyW, s.bodyY - 2, 8, s.bodyH + 4, p.subC);
  fillRect(data, s.bodyX + s.bodyW + 4, s.bodyY - 4, 4, s.bodyH + 8, p.bodyShadowC);
  // 背鳍（顶部）
  fillRect(data, s.bodyX + 6, s.bodyY - 4, 12, 4, p.subC);
  // 眼
  fillRect(data, s.bodyX + 4, s.bodyY + 4, 3, 3, p.eyeC);
  fillRect(data, s.bodyX + 5, s.bodyY + 5, 1, 1, p.darkC);
  // 嘴
  fillRect(data, s.bodyX, s.bodyY + s.bodyH / 2, 3, 1, p.darkC);
  // 变体 1：鳞片纹路
  if (variant === 1) {
    for (let i = 0; i < 3; i++) {
      fillRect(data, s.bodyX + 8 + i * 8, s.bodyY + 4, 2, 2, p.subC);
    }
  }
  // 变体 2：腹部亮色
  if (variant === 2) {
    fillRect(data, s.bodyX + 6, s.bodyY + s.bodyH - 3, s.bodyW - 8, 2, p.bodyLightC);
  }
}

const DRAWERS: Record<BodyType, (data: ImageData, variant: 0 | 1 | 2, p: PaletteEx, features: Set<string>) => void> = {
  humanoid: (d, v, p, f) => drawHumanoid(d, v, p, f),
  beast: (d, v, p) => drawBeast(d, v, p),
  abstract: (d, v, p) => drawAbstract(d, v, p),
  multi_leg: (d, v, p) => drawMultiLeg(d, v, p),
  winged: (d, v, p) => drawWinged(d, v, p),
  blob: (d, v, p) => drawBlob(d, v, p),
  tall: (d, v, p) => drawTall(d, v, p),
  stout: (d, v, p) => drawStout(d, v, p),
  snake: (d, v, p) => drawSnake(d, v, p),
  spider: (d, v, p) => drawSpider(d, v, p),
  crystal: (d, v, p) => drawCrystal(d, v, p),
  fish: (d, v, p) => drawFish(d, v, p),
};

// === 根据 features 推断体型（带权重） ===
function inferBodyType(features: string[], rng: Rng): BodyType {
  const set = new Set(features);
  const scored: Array<[BodyType, number]> = [
    ["winged", (set.has("翅膀") || set.has("wing") || set.has("革翼") || set.has("翅肢")) ? 5 : 1],
    ["multi_leg", (set.has("多足") || set.has("虫足") || set.has("触手")) ? 5 : 1],
    ["beast", (set.has("兽形") || set.has("兽足") || set.has("兽")) ? 4 : 1],
    ["humanoid", (set.has("人形") || set.has("人形身")) ? 4 : 1],
    ["tall", (set.has("直立") || set.has("高")) ? 2 : 1],
    ["stout", (set.has("趴伏") || set.has("蜷缩") || set.has("病态")) ? 2 : 1],
    ["abstract", (set.has("集合体") || set.has("抽象") || set.has("不定形") || set.has("扭曲")) ? 4 : 1],
    ["blob", 1],
  ];
  const types = scored.map(([t]) => t);
  const weights = scored.map(([, w]) => w);
  return rng.pickByWeight(types, weights);
}

export interface MonsterFrames {
  frame1: ImageData;
  frame2: ImageData;
  palette: PaletteEx;
  bodyType: BodyType;
  variant: number;
  hornStyle: HornStyle;
  eyeStyle: EyeStyle;
  mouthStyle: MouthStyle;
  wingStyle: WingStyle;
  seed: number;
}

export function generateMonster(
  desc: MonsterDesc,
  seed?: number,
  bodyTypeOverride?: BodyType,
  paletteNameOverride?: string,
  variantOverride?: 0 | 1 | 2
): MonsterFrames {
  const s = seed ?? hashStr(desc.scpId);
  const rng = new Rng(s);
  const features = new Set(desc.features);
  const bodyType = bodyTypeOverride ?? inferBodyType([...features], rng);
  const variant = variantOverride ?? (rng.int(0, 2) as 0 | 1 | 2);
  const palette = paletteNameOverride
    ? EXTENDED_PALETTES.find((p) => p.name === paletteNameOverride) ?? rng.pick(EXTENDED_PALETTES)
    : rng.pick(EXTENDED_PALETTES);
  const p = derivePalette(palette, rng);

  const hornStyle: HornStyle = (() => {
    if (features.has("王冠") || features.has("crown")) return "crown";
    if (features.has("龙角") || features.has("dragon_horn")) return "spiral";
    if (features.has("golden_horn")) return "wide";
    if (features.has("角") || features.has("骨刺")) return rng.pick<HornStyle>(["pair", "single", "twin_dot", "wide"]);
    if (rng.next() < 0.3) return rng.pick<HornStyle>(["pair", "single", "twin_dot"]);
    return "none";
  })();

  const eyeStyle: EyeStyle = (() => {
    if (features.has("多眼")) return "many";
    if (features.has("发光")) return "sparkle";
    if (features.has("病态")) return "x";
    if (features.has("透明")) return "tall";
    return rng.pick<EyeStyle>(["double", "double", "double", "single", "big", "sparkle", "tall", "closed"]);
  })();

  const mouthStyle: MouthStyle = (() => {
    if (features.has("獠牙") || features.has("骨刺")) return "fang";
    if (features.has("血")) return "tongue";
    if (features.has("病态")) return "stitch";
    if (features.has("透明") || features.has("幽灵")) return "sealed";
    return rng.pick<MouthStyle>(["smile", "smile", "open", "open", "frown", "fang", "stitch", "tongue"]);
  })();

  const wingStyle: WingStyle = (() => {
    if (features.has("翅膀") || features.has("wing") || features.has("革翼") || features.has("翅肢")) {
      return rng.pick<WingStyle>(["fairy", "bat", "feather", "jelly"]);
    }
    return "none";
  })();

  const frame1 = blankCanvas();
  DRAWERS[bodyType](frame1, variant, p, features);

  // 不同体型的"头部"位置不同，眼睛和嘴要画在正确位置
  // [skipEyes, headX, headY]：蛇/鱼/蜘蛛/水晶已自带眼睛，跳过叠加
  const headInfo: Record<BodyType, [boolean, number, number]> = {
    humanoid: [false, 23, 8],
    beast: [false, 50, 20],
    abstract: [false, 27, 20],
    multi_leg: [false, 8, 28],
    winged: [false, 27, 8],
    blob: [false, 27, 26],
    tall: [false, 27, 6],
    stout: [false, 22, 12],
    snake: [true, 0, 0],   // 自带眼
    spider: [true, 0, 0],  // 自带 4 眼
    crystal: [true, 0, 0],  // 无眼
    fish: [false, 18, 30], // 鱼头
  };
  const [skipEyes, headX, headY] = headInfo[bodyType];
  if (!skipEyes) {
    drawEyes(frame1, eyeStyle, p, headX, headY);
    drawMouth(frame1, mouthStyle, p, headX, headY);
  }
  // 角（所有体型都画）
  drawHorn(frame1, hornStyle, p, rng);
  // 翅膀（所有体型都画）
  drawWings(frame1, wingStyle, p);

  // 触须
  if (features.has("触须") || features.has("thread_tendril")) {
    for (let i = 0; i < 4; i++) {
      fillRect(frame1, 18 - i, 28 + i * 2, 2, 2, p.bodyShadowC);
      fillRect(frame1, 46 + i, 28 + i * 2, 2, 2, p.bodyShadowC);
    }
  }

  // 程序化扰动
  if (rng.next() < 0.7) {
    perturb(frame1, rng, rng.int(2, 8), p.bodyShadowC);
  }

  // 帧 2：基准 + 整体下移 1 像素
  const frame2 = blankCanvas();
  for (let y = 0; y < 64; y++) {
    for (let x = 0; x < 64; x++) {
      if (y + 1 < 64) {
        const i = (y * 64 + x) * 4;
        const j = ((y + 1) * 64 + x) * 4;
        frame2.data[j] = frame1.data[i];
        frame2.data[j + 1] = frame1.data[i + 1];
        frame2.data[j + 2] = frame1.data[i + 2];
        frame2.data[j + 3] = frame1.data[i + 3];
      }
    }
  }

  return {
    frame1,
    frame2,
    palette: p,
    bodyType,
    variant,
    hornStyle,
    eyeStyle,
    mouthStyle,
    wingStyle,
    seed: s,
  };
}

export function drawToCanvas(canvas: HTMLCanvasElement, data: ImageData, scale: number = 1): void {
  canvas.width = 64 * scale;
  canvas.height = 64 * scale;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  const tmp = document.createElement("canvas");
  tmp.width = 64;
  tmp.height = 64;
  tmp.getContext("2d")!.putImageData(data, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(tmp, 0, 0, 64 * scale, 64 * scale);
}

export function animateIdle(
  canvas: HTMLCanvasElement,
  frames: MonsterFrames,
  scale: number = 1
): () => void {
  let i = 0;
  let timer: number | null = null;
  const tick = () => {
    drawToCanvas(canvas, i % 2 === 0 ? frames.frame1 : frames.frame2, scale);
    i++;
  };
  tick();
  timer = window.setInterval(tick, 167);
  return () => {
    if (timer !== null) window.clearInterval(timer);
  };
}
